import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Radio, Users, FileText, Loader2, Lock } from 'lucide-react';
import { PremiumCardOverlay } from '@/components/PremiumLock';

function Channels() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [followedIds, setFollowedIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const channelsRes = await api.get('/channels');
        setChannels(channelsRes.data);

        if (user) {
          const followedRes = await api.get('/my-followed-channels');
          const ids = [];
          for (let i = 0; i < followedRes.data.length; i++) {
            ids.push(followedRes.data[i].id);
          }
          setFollowedIds(ids);
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [user]);

  async function handleFollow(channelId) {
    if (!user) {
      navigate('/login');
      return;
    }

    const channel = channels.find(c => c.id === channelId);
    if (channel?.is_locked) {
      navigate('/premium');
      return;
    }

    const isFollowing = followedIds.indexOf(channelId) !== -1;

    try {
      if (isFollowing) {
        await api.delete('/channels/' + channelId + '/follow');
        const newIds = [];
        for (let i = 0; i < followedIds.length; i++) {
          if (followedIds[i] !== channelId) {
            newIds.push(followedIds[i]);
          }
        }
        setFollowedIds(newIds);
        toast.success('Deixou de seguir o canal');
      } else {
        await api.post('/channels/' + channelId + '/follow');
        setFollowedIds(followedIds.concat([channelId]));
        toast.success('Está a seguir o canal!');
      }
    } catch (err) {
      toast.error('Erro ao atualizar');
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ruby" />
      </div>
    );
  }

  const channelCards = [];
  for (let i = 0; i < channels.length; i++) {
    const channel = channels[i];
    const isFollowing = followedIds.indexOf(channel.id) !== -1;

    channelCards.push(
      <div key={channel.id} className="relative bg-surface rounded-xl border border-border overflow-hidden hover:border-ruby/30 transition-all duration-300">
        {channel.is_locked && <PremiumCardOverlay />}
        {channel.cover_url && (
          <div className="h-32 overflow-hidden">
            <img src={channel.cover_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border-2 border-ruby/30">
              <img 
                src={channel.avatar_url || 'https://images.unsplash.com/photo-1518893063132-36e46dbe2428?w=200'} 
                alt={channel.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <Link to={channel.is_locked ? '/premium' : ('/channel/' + (channel.slug || channel.id))} className="block">
                <h3 className="font-brand text-lg font-semibold text-foreground hover:text-ruby transition-colors truncate flex items-center gap-2">
                  {channel.name}
                  {channel.is_locked && <Lock className="w-4 h-4 text-gold" />}
                </h3>
              </Link>
              {channel.description && (
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{channel.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {channel.follower_count} seguidores
            </span>
            <span className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              {channel.post_count} posts
            </span>
          </div>

          <Button
            onClick={function() { handleFollow(channel.id); }}
            data-testid={'follow-' + channel.id}
            className={'w-full mt-4 rounded-full ' + (channel.is_locked ? 'bg-gradient-to-r from-gold to-gold-light text-black hover:from-gold-light hover:to-gold' : (isFollowing ? 'bg-surface-light border border-ruby text-ruby hover:bg-ruby/10' : 'bg-ruby hover:bg-ruby-light text-white'))}
          >
            {channel.is_locked ? (
              <span className="flex items-center justify-center gap-2"><Lock className="w-4 h-4" />Assinar Premium</span>
            ) : (isFollowing ? 'Seguindo' : 'Seguir')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-ruby/10 flex items-center justify-center">
              <Radio className="w-6 h-6 text-ruby" />
            </div>
            <div>
              <h1 className="font-brand text-2xl font-bold text-foreground">Canais</h1>
              <p className="text-muted-foreground">Segue canais e recebe novidades no teu feed</p>
            </div>
          </div>

          {user && followedIds.length > 0 && (
            <div className="mb-8">
              <Button
                onClick={function() { navigate('/feed'); }}
                data-testid="go-to-feed"
                className="bg-ruby hover:bg-ruby-light rounded-full"
              >
                Ver meu Feed ({followedIds.length} canais seguidos)
              </Button>
            </div>
          )}

          {channels.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {channelCards}
            </div>
          ) : (
            <div className="text-center py-20">
              <Radio className="w-16 h-16 text-muted mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Nenhum canal disponível</h2>
              <p className="text-muted-foreground">Os administradores ainda não criaram canais.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default Channels;
